import { supabase, getCurrentUser } from './supabase';

const BUCKET = 'car-gallery';
const TABLE = 'car_gallery_images';
const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;

export type CarImageMetadata = {
  title?: string;
  description?: string;
  takenAt?: string | null;
};

type CarImageRecordInput = {
  file_path: string;
  tags?: string[];
  metadata?: CarImageMetadata;
};

// Upload image to car-gallery bucket under user folder
export async function uploadCarImage(
  file: File,
  tags: string[] = [],
  metadata: CarImageMetadata = {}
): Promise<{ data: any; error: any }> {
  const user = await getCurrentUser();
  if (!user) return { data: null, error: 'Not authenticated' };
  if (!file.type.startsWith('image/')) return { data: null, error: 'Only image files are allowed' };
  if (file.size > MAX_UPLOAD_SIZE_BYTES) return { data: null, error: 'Each image must be 10MB or smaller' };

  const fileExt = file.name.split('.').pop();
  const fileName = `${Date.now()}.${fileExt}`;
  const filePath = `${user.id}/${fileName}`;
  const { data, error } = await supabase.storage.from(BUCKET).upload(filePath, file, {
    cacheControl: '3600',
    upsert: false,
  });
  if (!error) {
    await addImageRecord({ file_path: filePath, tags, metadata });
  }
  return { data, error };
}

// Add image record to DB
export async function addImageRecord(payload: CarImageRecordInput): Promise<{ data: any; error: any }> {
  const user = await getCurrentUser();
  if (!user) return { data: null, error: 'Not authenticated' };
  const trimmedTitle = payload.metadata?.title?.trim() || null;
  const trimmedDescription = payload.metadata?.description?.trim() || null;
  return await supabase.from(TABLE).insert([{
    user_id: user.id,
    file_path: payload.file_path,
    tags: payload.tags || [],
    title: trimmedTitle,
    description: trimmedDescription,
    taken_at: payload.metadata?.takenAt || null,
  }]);
}

// Get all images for current user (with tags), with signed URLs
export async function getGalleryImages(): Promise<{ data: any[]; error: any }> {
  const user = await getCurrentUser();
  if (!user) return { data: [], error: 'Not authenticated' };
  const { data, error } = await supabase.from(TABLE).select('*').eq('user_id', user.id).order('created_at', { ascending: false });
  if (error) return { data: [], error };
  // For each image, generate a signed URL (valid for 1 hour)
  const images = await Promise.all((data || []).map(async (item: any) => {
    const { data: signedData } = await supabase.storage.from(BUCKET).createSignedUrl(item.file_path, 60 * 60);
    return {
      ...item,
      url: signedData?.signedUrl,
    };
  }));
  return { data: images, error: null };
}

// Add a tag to an image
export async function addTagToImage(file_path: string, tag: string): Promise<{ data: any; error: any }> {
  const user = await getCurrentUser();
  if (!user) return { data: null, error: 'Not authenticated' };
  // Get current tags
  const { data, error } = await supabase.from(TABLE).select('tags').eq('user_id', user.id).eq('file_path', file_path).single();
  if (error) return { data: null, error };
  const tags: string[] = data.tags || [];
  if (!tags.includes(tag)) tags.push(tag);
  return await supabase.from(TABLE).update({ tags }).eq('user_id', user.id).eq('file_path', file_path);
}

// Remove a tag from an image
export async function removeTagFromImage(file_path: string, tag: string): Promise<{ data: any; error: any }> {
  const user = await getCurrentUser();
  if (!user) return { data: null, error: 'Not authenticated' };
  const { data, error } = await supabase.from(TABLE).select('tags').eq('user_id', user.id).eq('file_path', file_path).single();
  if (error) return { data: null, error };
  const tags: string[] = (data.tags || []).filter((t: string) => t !== tag);
  return await supabase.from(TABLE).update({ tags }).eq('user_id', user.id).eq('file_path', file_path);
}

// Update all tags for an image
export async function updateTagsForImage(file_path: string, tags: string[]): Promise<{ data: any; error: any }> {
  const user = await getCurrentUser();
  if (!user) return { data: null, error: 'Not authenticated' };
  return await supabase.from(TABLE).update({ tags }).eq('user_id', user.id).eq('file_path', file_path);
}

// Update photo metadata
export async function updateImageMetadata(
  file_path: string,
  metadata: CarImageMetadata
): Promise<{ data: any; error: any }> {
  const user = await getCurrentUser();
  if (!user) return { data: null, error: 'Not authenticated' };

  return await supabase
    .from(TABLE)
    .update({
      title: metadata.title?.trim() || null,
      description: metadata.description?.trim() || null,
      taken_at: metadata.takenAt || null,
    })
    .eq('user_id', user.id)
    .eq('file_path', file_path);
}

// Get all unique tags for current user (for filter bar)
export async function getAllTags(): Promise<{ data: string[]; error: any }> {
  const user = await getCurrentUser();
  if (!user) return { data: [], error: 'Not authenticated' };
  const { data, error } = await supabase.from(TABLE).select('tags').eq('user_id', user.id);
  if (error) return { data: [], error };
  const allTags = (data || []).flatMap((row: any) => row.tags || []);
  const uniqueTags = Array.from(new Set(allTags));
  return { data: uniqueTags, error: null };
}

// Delete image by path (from storage and db)
export async function deleteCarImage(path: string): Promise<{ data: any; error: any }> {
  // Remove from storage
  const { data: storageData, error: storageError } = await supabase.storage.from(BUCKET).remove([path]);
  // Remove from db
  const user = await getCurrentUser();
  let dbError = null;
  if (user) {
    const { error: dbErr } = await supabase.from(TABLE).delete().eq('user_id', user.id).eq('file_path', path);
    dbError = dbErr;
  }
  return { data: storageData, error: storageError || dbError };
} 